import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    type AutocompleteRenderInputParams,
    type SelectChangeEvent
} from '@mui/material'
import React from 'react'
import { Logger } from 'wdc-cube'
import { classToFComponent, FCClassContext, LifesHubConfirmDialog } from 'src/utils/views'
import {
    CONTRATO_FIELD_KEYS,
    CRM_TYPE_SUGGESTIONS,
    type CrmConnection,
    type CrmDirection,
    type FieldMappingEntry,
    type OutboundAuthType,
    type OutboundBodyAttribute,
    type OutboundHeader
} from '../../crm_integration_service'
import ProfileCard from './profile-card'
import type { CrmConnectionForm, CrmConnectionsScope } from '../../gp_scopes'

const LOG = Logger.get('CrmConnectionsSection')

type DirectionOption = { value: CrmDirection; label: string }

type AuthTypeOption = { value: OutboundAuthType; label: string }

type PairRow = { a: string; b: string }

const DIRECOES: DirectionOption[] = [
    { value: 'inbound', label: 'Entrada (Pull)' },
    { value: 'outbound', label: 'Saída (Push)' },
    { value: 'both', label: 'Ambos' }
]

const AUTH_TYPES: AuthTypeOption[] = [
    { value: 'header', label: 'Header customizado' },
    { value: 'api_key', label: 'API key' },
    { value: 'bearer', label: 'Bearer token' },
    { value: 'none', label: 'Sem autenticação' }
]

function headerToPair(header: OutboundHeader): PairRow {
    return { a: header.name, b: header.value }
}

function bodyAttributeToPair(attribute: OutboundBodyAttribute): PairRow {
    return { a: attribute.path, b: attribute.value }
}

function replaceAt<T>(rows: T[], index: number, patch: Partial<T>): T[] {
    const next = rows.slice()
    next[index] = { ...next[index], ...patch }
    return next
}

function removeAt<T>(rows: T[], index: number): T[] {
    const next = rows.slice()
    next.splice(index, 1)
    return next
}

function targetOf(entries: FieldMappingEntry[], sourceKey: string): string {
    for (const entry of entries) {
        if (entry.source === sourceKey) {
            return entry.target
        }
    }
    return ''
}

function withoutSource(entries: FieldMappingEntry[], sourceKey: string): FieldMappingEntry[] {
    const next: FieldMappingEntry[] = []
    for (const entry of entries) {
        if (entry.source !== sourceKey) {
            next.push(entry)
        }
    }
    return next
}

function countMapped(entries: FieldMappingEntry[]): number {
    let total = 0
    for (const entry of entries) {
        if (entry.target.trim() !== '') {
            total++
        }
    }
    return total
}

export type CrmConnectionsSectionProps = {
    className?: string
    scope: CrmConnectionsScope
}

class CrmConnectionsSectionClass implements FCClassContext<CrmConnectionsSectionProps> {
    readonly emitNewClick = () => {
        this.scope.onNew().catch(LOG.caught)
    }

    readonly emitEditClick = (connectionId: number) => () => {
        this.scope.onEdit(connectionId).catch(LOG.caught)
    }

    readonly emitDeleteClick = (connectionId: number) => () => {
        this.scope.onDelete(connectionId).catch(LOG.caught)
    }

    readonly emitCancelForm = () => {
        this.scope.onCancelForm().catch(LOG.caught)
    }

    readonly emitSubmit = () => {
        this.scope.onSubmit().catch(LOG.caught)
    }

    readonly emitConfirmDelete = () => {
        this.scope.onConfirmDelete().catch(LOG.caught)
    }

    readonly emitCancelDelete = () => {
        this.scope.onCancelDelete().catch(LOG.caught)
    }

    readonly emitFormChange = (patch: Partial<CrmConnectionForm>) => {
        this.scope.onFormChange(patch).catch(LOG.caught)
    }

    readonly emitFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.emitFormChange({ [e.target.name]: e.target.value } as unknown as Partial<CrmConnectionForm>)
    }

    readonly emitCrmTypeInputChange = (_event: React.SyntheticEvent, crm_type: string) => {
        this.emitFormChange({ crm_type })
    }

    readonly emitDirectionChange = (e: SelectChangeEvent<CrmDirection>) => {
        this.emitFormChange({ direction: e.target.value as CrmDirection })
    }

    readonly emitAuthTypeChange = (e: SelectChangeEvent<OutboundAuthType>) => {
        this.emitFormChange({ authType: e.target.value as OutboundAuthType })
    }

    readonly emitHeaderAdd = () => {
        this.emitFormChange({ headers: [...this.scope.form.headers, { name: '', value: '' }] })
    }

    readonly emitHeaderNameChange = (index: number, value: string) => {
        this.emitFormChange({ headers: replaceAt(this.scope.form.headers, index, { name: value }) })
    }

    readonly emitHeaderValueChange = (index: number, value: string) => {
        this.emitFormChange({ headers: replaceAt(this.scope.form.headers, index, { value }) })
    }

    readonly emitHeaderRemove = (index: number) => {
        this.emitFormChange({ headers: removeAt(this.scope.form.headers, index) })
    }

    readonly emitBodyAttributeAdd = () => {
        this.emitFormChange({ bodyAttributes: [...this.scope.form.bodyAttributes, { path: '', value: '' }] })
    }

    readonly emitBodyAttributePathChange = (index: number, value: string) => {
        this.emitFormChange({ bodyAttributes: replaceAt(this.scope.form.bodyAttributes, index, { path: value }) })
    }

    readonly emitBodyAttributeValueChange = (index: number, value: string) => {
        this.emitFormChange({ bodyAttributes: replaceAt(this.scope.form.bodyAttributes, index, { value }) })
    }

    readonly emitBodyAttributeRemove = (index: number) => {
        this.emitFormChange({ bodyAttributes: removeAt(this.scope.form.bodyAttributes, index) })
    }

    readonly emitFieldMappingChange = (sourceKey: string, value: string) => {
        const rest = withoutSource(this.scope.form.fieldMapping, sourceKey)
        this.emitFormChange({ fieldMapping: value === '' ? rest : [...rest, { source: sourceKey, target: value }] })
    }

    scope!: CrmConnectionsScope

    onSyncState({ scope }: CrmConnectionsSectionProps) {
        this.scope = scope
    }

    readonly renderCrmTypeInput = (params: AutocompleteRenderInputParams) => (
        <TextField
            {...params}
            label="CRM"
            placeholder="Ex.: PLOOMES, PIPEDRIVE, CRM_PROPRIO"
            helperText="Rótulo livre — a integração é configurada abaixo"
        />
    )

    readonly renderDirectionOption = (option: DirectionOption) => (
        <MenuItem key={option.value} value={option.value}>
            {option.label}
        </MenuItem>
    )

    readonly renderAuthTypeOption = (option: AuthTypeOption) => (
        <MenuItem key={option.value} value={option.value}>
            {option.label}
        </MenuItem>
    )

    readonly renderConnectionRow = (conn: CrmConnection) => (
        <TableRow key={conn.id}>
            <TableCell>{conn.name}</TableCell>
            <TableCell>{conn.crm_type}</TableCell>
            <TableCell>{conn.direction}</TableCell>
            <TableCell>{conn.outbound_config?.endpoint_base || '—'}</TableCell>
            <TableCell align="right">
                <Tooltip title="Editar conexão">
                    <IconButton color="info" onClick={this.emitEditClick(conn.id)}>
                        <EditIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Remover conexão">
                    <IconButton color="error" onClick={this.emitDeleteClick(conn.id)}>
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            </TableCell>
        </TableRow>
    )

    render({ className }: CrmConnectionsSectionProps) {
        const scope = this.scope
        const form = scope.form
        const headerRows = form.headers.map(headerToPair)
        const bodyAttributeRows = form.bodyAttributes.map(bodyAttributeToPair)

        return (
            <ProfileCard caption="Integração CRM :: Conexões" className={className}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button variant="contained" color="success" startIcon={<AddIcon />} onClick={this.emitNewClick}>
                        Nova conexão
                    </Button>
                </Box>

                {scope.loading && <LinearProgress color="secondary" />}

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Nome</TableCell>
                            <TableCell>CRM</TableCell>
                            <TableCell>Sentido</TableCell>
                            <TableCell>Endpoint</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {scope.connections.length === 0 && !scope.loading && (
                            <TableRow>
                                <TableCell colSpan={5}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.secondary'
                                        }}
                                    >
                                        Nenhuma conexão cadastrada.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {scope.connections.map(this.renderConnectionRow)}
                    </TableBody>
                </Table>

                <Dialog open={scope.formOpen} onClose={this.emitCancelForm} maxWidth="md" fullWidth>
                    <DialogTitle>{scope.editingId ? 'Editar conexão de CRM' : 'Nova conexão de CRM'}</DialogTitle>
                    <DialogContent dividers>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Autocomplete
                                    freeSolo
                                    options={CRM_TYPE_SUGGESTIONS as readonly string[]}
                                    inputValue={form.crm_type}
                                    onInputChange={this.emitCrmTypeInputChange}
                                    renderInput={this.renderCrmTypeInput}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 5 }}>
                                <TextField
                                    fullWidth
                                    label="Nome da conexão"
                                    name="name"
                                    value={form.name}
                                    onChange={this.emitFieldChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <FormControl fullWidth>
                                    <InputLabel id="direction-label">Sentido</InputLabel>
                                    <Select
                                        labelId="direction-label"
                                        label="Sentido"
                                        name="direction"
                                        value={form.direction}
                                        onChange={this.emitDirectionChange}
                                    >
                                        {DIRECOES.map(this.renderDirectionOption)}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={12}>
                                <Divider textAlign="left">
                                    <Typography variant="subtitle2">Configuração de saída (outbound_config)</Typography>
                                </Divider>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Endpoint base (URL)"
                                    placeholder="https://api.ploomes.com"
                                    name="endpoint_base"
                                    value={form.endpoint_base}
                                    onChange={this.emitFieldChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Path do recurso"
                                    placeholder="/Contacts"
                                    name="path"
                                    value={form.path}
                                    onChange={this.emitFieldChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 2 }}>
                                <TextField
                                    fullWidth
                                    label="Método"
                                    placeholder="POST"
                                    name="method"
                                    value={form.method}
                                    onChange={this.emitFieldChange}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 4 }}>
                                <FormControl fullWidth>
                                    <InputLabel id="auth-type-label">Tipo de autenticação</InputLabel>
                                    <Select
                                        labelId="auth-type-label"
                                        label="Tipo de autenticação"
                                        name="authType"
                                        value={form.authType}
                                        onChange={this.emitAuthTypeChange}
                                    >
                                        {AUTH_TYPES.map(this.renderAuthTypeOption)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Nome do header de auth"
                                    placeholder="User-Key / Authorization"
                                    name="authHeaderName"
                                    value={form.authHeaderName}
                                    disabled={form.authType === 'none'}
                                    onChange={this.emitFieldChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    fullWidth
                                    type="password"
                                    label="Token / segredo de auth"
                                    placeholder={scope.editingId ? '•••• (mantém o atual)' : 'informe o segredo'}
                                    name="authToken"
                                    value={form.authToken}
                                    disabled={form.authType === 'none'}
                                    autoComplete="new-password"
                                    onChange={this.emitFieldChange}
                                />
                            </Grid>

                            <Grid size={12}>
                                <DynamicPairList
                                    title="Headers adicionais"
                                    rows={headerRows}
                                    labelA="Nome"
                                    labelB="Valor"
                                    onAdd={this.emitHeaderAdd}
                                    onChangeA={this.emitHeaderNameChange}
                                    onChangeB={this.emitHeaderValueChange}
                                    onRemove={this.emitHeaderRemove}
                                />
                            </Grid>

                            <Grid size={12}>
                                <DynamicPairList
                                    title="Atributos de body (fixos)"
                                    rows={bodyAttributeRows}
                                    labelA="Path"
                                    labelB="Valor"
                                    onAdd={this.emitBodyAttributeAdd}
                                    onChangeA={this.emitBodyAttributePathChange}
                                    onChangeB={this.emitBodyAttributeValueChange}
                                    onRemove={this.emitBodyAttributeRemove}
                                />
                            </Grid>

                            <Grid size={12}>
                                <FieldMappingTable
                                    entries={form.fieldMapping}
                                    onTargetChange={this.emitFieldMappingChange}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button color="inherit" onClick={this.emitCancelForm}>
                            Cancelar
                        </Button>
                        <Button variant="contained" color="success" disabled={scope.saving} onClick={this.emitSubmit}>
                            Salvar
                        </Button>
                    </DialogActions>
                </Dialog>

                <LifesHubConfirmDialog
                    open={!!scope.deleteTarget}
                    title="Remover conexão"
                    subtitle={scope.deleteTarget?.name}
                    tone="danger"
                    message="As API-keys associadas a esta conexão deixam de funcionar."
                    confirmLabel="Remover conexão"
                    onConfirm={this.emitConfirmDelete}
                    onCancel={this.emitCancelDelete}
                />
            </ProfileCard>
        )
    }
}

type DynamicPairListProps = {
    title: string
    rows: PairRow[]
    labelA: string
    labelB: string
    onAdd: () => void
    onChangeA: (index: number, value: string) => void
    onChangeB: (index: number, value: string) => void
    onRemove: (index: number) => void
}

class DynamicPairListClass implements FCClassContext<DynamicPairListProps> {
    readonly emitAdd = () => {
        this.props.onAdd()
    }

    readonly emitChangeA = (index: number) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.props.onChangeA(index, e.target.value)
    }

    readonly emitChangeB = (index: number) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.props.onChangeB(index, e.target.value)
    }

    readonly emitRemove = (index: number) => () => {
        this.props.onRemove(index)
    }

    props!: DynamicPairListProps

    onSyncState(props: DynamicPairListProps) {
        this.props = props
    }

    readonly renderPairRow = (row: PairRow, index: number) => (
        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
                sx={{ flex: 1 }}
                size="small"
                label={this.props.labelA}
                value={row.a}
                onChange={this.emitChangeA(index)}
            />
            <TextField
                sx={{ flex: 1 }}
                size="small"
                label={this.props.labelB}
                value={row.b}
                onChange={this.emitChangeB(index)}
            />
            <IconButton color="error" onClick={this.emitRemove(index)}>
                <DeleteIcon />
            </IconButton>
        </Box>
    )

    render({ title, rows }: DynamicPairListProps) {
        return (
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2">{title}</Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={this.emitAdd}>
                        Adicionar
                    </Button>
                </Box>
                {rows.map(this.renderPairRow)}
            </Paper>
        )
    }
}

const DynamicPairList = classToFComponent(DynamicPairListClass, React)

type FieldMappingTableProps = {
    entries: FieldMappingEntry[]
    onTargetChange: (sourceKey: string, value: string) => void
}

class FieldMappingTableClass implements FCClassContext<FieldMappingTableProps> {
    readonly emitTargetChange =
        (sourceKey: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            this.props.onTargetChange(sourceKey, e.target.value)
        }

    props!: FieldMappingTableProps

    onSyncState(props: FieldMappingTableProps) {
        this.props = props
    }

    readonly renderFieldRow = (sourceKey: string) => (
        <TableRow key={sourceKey}>
            <TableCell sx={{ fontFamily: 'monospace', width: '40%' }}>{sourceKey}</TableCell>
            <TableCell>
                <TextField
                    fullWidth
                    size="small"
                    variant="standard"
                    placeholder="não enviar"
                    value={targetOf(this.props.entries, sourceKey)}
                    onChange={this.emitTargetChange(sourceKey)}
                />
            </TableCell>
        </TableRow>
    )

    render({ entries }: FieldMappingTableProps) {
        const mapeados = countMapped(entries)

        return (
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Mapeamento de campos (field_mapping)</Typography>
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        display: 'block',
                        mb: 1
                    }}
                >
                    Sem nenhum destino preenchido, o contrato é enviado inteiro (passthrough). Preencha o destino dos
                    campos que quer enviar; deixe em branco para NÃO enviar aquele campo. <strong>{mapeados}</strong> de{' '}
                    {CONTRATO_FIELD_KEYS.length} mapeados.
                </Typography>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Campo do contrato (origem)</TableCell>
                            <TableCell>Chave no CRM (destino) — em branco = não enviar</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>{CONTRATO_FIELD_KEYS.map(this.renderFieldRow)}</TableBody>
                </Table>
            </Paper>
        )
    }
}

const FieldMappingTable = classToFComponent(FieldMappingTableClass, React)

export const CrmConnectionsSection = classToFComponent(CrmConnectionsSectionClass, React)
export default CrmConnectionsSection
